/**
 * The AI analysis in full.
 *
 * The strategy agent produces more than a list: it produces an argument — what
 * to do first, which of the site's own signals contradict each other, what
 * order the work should happen in, and what it was never told. The dashboard
 * rail can only carry a summary of that, so the whole thing lives here.
 *
 * Two rules shape the presentation. Every priority shows the deterministic
 * check ids it is grounded in, as links you can follow to the underlying
 * findings — an interpretation you cannot trace back is one you cannot check.
 * And nothing here is an action: these are not the typed changes the executor
 * can perform and verify, and reading this page changes nothing on the site.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { aiApi, siteApi } from '@/api/endpoints';
import {
  AlertIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  InfoIcon,
  PlayIcon,
  SparkIcon,
  TrendIcon,
} from '@/components/icons';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  ConfidenceRing,
  DataRow,
  DetailLayout,
  DetailMain,
  DetailRail,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Spinner,
} from '@/components/ui';
import { cn, confidenceTone, formatNumber, formatRelative } from '@/utils/format';
import type {
  AgentRunDetail,
  AgentRunSummary,
  DashboardResponse,
  LlmCallSummary,
  StrategicPriority,
} from '@/types/api';

const VISIBLE_GAPS = 4;

export function AiAnalysisPage() {
  const { siteId = '' } = useParams();
  const queryClient = useQueryClient();

  const statusQuery = useQuery({ queryKey: ['ai-status'], queryFn: () => aiApi.status() });
  const dashboardQuery = useQuery({
    queryKey: ['dashboard', siteId],
    queryFn: () => siteApi.dashboard(siteId),
  });

  const runsQuery = useQuery({
    queryKey: ['ai-runs', siteId],
    queryFn: () => aiApi.list(siteId, { page_size: 5 }),
    refetchInterval: (query) => {
      const page = query.state.data as { items: AgentRunSummary[] } | undefined;
      const status = page?.items?.[0]?.status;
      return status === 'running' || status === 'queued' ? 3000 : false;
    },
  });

  const latest = runsQuery.data?.items?.[0];

  const detailQuery = useQuery({
    queryKey: ['ai-run', latest?.id],
    queryFn: () => aiApi.get(latest!.id),
    enabled: Boolean(latest && latest.status === 'completed'),
  });

  const callsQuery = useQuery({
    queryKey: ['ai-run', latest?.id, 'calls'],
    queryFn: () => aiApi.calls(latest!.id),
    enabled: Boolean(latest && latest.status === 'completed'),
  });

  const runMutation = useMutation({
    mutationFn: () => aiApi.start(siteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-runs', siteId] }),
  });

  const dashboard = dashboardQuery.data as DashboardResponse | undefined;
  const hasAudit = Boolean(dashboard?.last_audit);
  const enabled = statusQuery.data?.enabled ?? false;
  const inFlight = latest?.status === 'queued' || latest?.status === 'running';

  if (statusQuery.isLoading || runsQuery.isLoading) return <LoadingState />;
  if (runsQuery.isError) {
    return (
      <ErrorState
        message={(runsQuery.error as Error).message}
        onRetry={() => void runsQuery.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="AI analysis"
        description="Strategic interpretation of the audit. Everything here is reading material — no change is made to your website from this page."
        actions={
          enabled ? (
            <Button
              variant="primary"
              onClick={() => runMutation.mutate()}
              loading={runMutation.isPending}
              disabled={!hasAudit || inFlight}
              title={
                !hasAudit
                  ? 'Run an audit first'
                  : inFlight
                    ? 'An analysis is already running'
                    : undefined
              }
              icon={<PlayIcon className="h-4 w-4" />}
            >
              {inFlight ? 'Analysing…' : latest ? 'Run again' : 'Run analysis'}
            </Button>
          ) : null
        }
      />

      {runMutation.isError ? (
        <div className="mb-5">
          <Callout tone="danger" icon={<AlertIcon className="h-5 w-5" />}>
            {runMutation.error instanceof ApiError
              ? runMutation.error.message
              : 'The analysis could not be started.'}
          </Callout>
        </div>
      ) : null}

      {!enabled ? (
        <EmptyState
          icon={<SparkIcon className="h-5 w-5" />}
          title="AI analysis is not configured"
          description="Crawling, auditing, scoring, actions, execution and verification all work without it. An AI provider key adds strategic interpretation and generated copy on top."
        />
      ) : !latest ? (
        <EmptyState
          icon={<SparkIcon className="h-5 w-5" />}
          title="No analysis yet"
          description={
            hasAudit
              ? 'Run an analysis to interpret the latest audit: what to do first, which signals contradict each other, and in what order.'
              : 'Run a crawl and an audit first. The analysis interprets audit findings rather than replacing them.'
          }
        />
      ) : inFlight ? (
        <RunningState run={latest} />
      ) : latest.status === 'failed' ? (
        <Callout
          tone="danger"
          title="The last analysis failed"
          icon={<AlertIcon className="h-5 w-5" />}
        >
          Nothing was changed on your website. You can run it again.
        </Callout>
      ) : detailQuery.isLoading ? (
        <LoadingState label="Loading the analysis" />
      ) : detailQuery.data ? (
        <Analysis
          siteId={siteId}
          run={detailQuery.data}
          calls={callsQuery.data ?? []}
          history={runsQuery.data?.items ?? []}
        />
      ) : null}
    </>
  );
}

function RunningState({ run }: { run: AgentRunSummary }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <Spinner className="h-5 w-5 text-brand" />
        <div>
          <p className="text-sm font-medium text-ink">
            {run.status === 'queued' ? 'Queued' : 'Analysing the latest audit'}
          </p>
          <p className="mt-0.5 text-sm text-ink-muted">
            Started {formatRelative(run.created_at)}
            {run.current_node ? ` · ${run.current_node.replace(/_/g, ' ')}` : ''}. This usually takes
            under a minute.
          </p>
        </div>
      </div>
    </Card>
  );
}

function Analysis({
  siteId,
  run,
  calls,
  history,
}: {
  siteId: string;
  run: AgentRunDetail;
  calls: LlmCallSummary[];
  history: AgentRunSummary[];
}) {
  const output = run.output_summary;
  const priorities = output.strategic_priorities ?? [];
  const conflicts = output.signal_conflicts ?? [];
  const sequence = output.recommended_sequence ?? [];
  const missing = output.missing_information ?? [];
  const confidence = output.overall_confidence ?? 0;

  return (
    <DetailLayout>
      <DetailMain>
        {/* ---------------------------------------------------------- hero */}
        <Card padded={false} className="overflow-hidden">
          <div className="flex flex-col gap-5 px-6 py-6 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-brand uppercase">
                <SparkIcon className="h-3.5 w-3.5" />
                The conclusion
              </p>
              {/* Set larger than body text: it is the one thing on the page
                  worth reading if you read nothing else. */}
              <p className="mt-2.5 text-[15px] leading-relaxed text-ink">
                {output.strategy_summary || 'The analysis returned no summary.'}
              </p>
            </div>
            <ConfidenceRing value={confidence} />
          </div>
          <div className="flex divide-x divide-border-subtle border-t border-border-subtle bg-surface-muted/50">
            <Stat label="Priorities" value={priorities.length} />
            <Stat label="Conflicts" value={conflicts.length} />
            <Stat label="Steps" value={sequence.length} />
            <Stat label="Findings read" value={output.findings_analysed ?? 0} />
          </div>
        </Card>

        {/* ---------------------------------------------------- priorities */}
        {priorities.length > 0 ? (
          <section>
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-xs font-semibold tracking-wider text-ink-subtle uppercase">
                  Strategic priorities
                </h2>
                <p className="mt-1 text-sm text-ink-muted">
                  In the order the analysis would tackle them.
                </p>
              </div>
              <Link
                to={`/sites/${siteId}/opportunities`}
                className="group inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                Scored recommendations
                <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
            <ol className="space-y-3">
              {priorities.map((priority, index) => (
                <PriorityCard
                  key={`${priority.title}-${index}`}
                  rank={index + 1}
                  priority={priority}
                  siteId={siteId}
                />
              ))}
            </ol>
          </section>
        ) : null}

        {/* ----------------------------------------------------- conflicts */}
        {conflicts.length > 0 ? (
          <Card>
            <CardHeader
              title="Signals that contradict each other"
              icon={<AlertIcon className="h-4 w-4" />}
              description="Places where the site tells search engines two different things."
            />
            <ul className="space-y-3">
              {conflicts.map((conflict, index) => (
                <li
                  key={`${conflict.description}-${index}`}
                  className="rounded-lg border border-border-subtle bg-surface-muted p-3.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm leading-relaxed text-ink">{conflict.description}</p>
                    <Badge
                      tone={
                        conflict.severity === 'high'
                          ? 'danger'
                          : conflict.severity === 'medium'
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {conflict.severity}
                    </Badge>
                  </div>
                  {conflict.urls?.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {conflict.urls.slice(0, 4).map((url) => (
                        <span
                          key={url}
                          className="max-w-full truncate rounded bg-surface px-1.5 py-0.5 font-mono text-[11px] text-ink-subtle"
                          title={url}
                        >
                          {url}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {/* ------------------------------------------------------ sequence */}
        {sequence.length > 0 ? (
          <Card>
            <CardHeader
              title="Recommended order of work"
              icon={<TrendIcon className="h-4 w-4" />}
              description="Sequencing matters: some of these only pay off once an earlier one is done."
            />
            <ol className="relative space-y-4 pl-7">
              {/* The spine makes the ordering read as a sequence rather than a list. */}
              <span
                aria-hidden="true"
                className="absolute top-2 bottom-2 left-[0.6875rem] w-px bg-border-subtle"
              />
              {sequence.map((step, index) => (
                <li key={`${step}-${index}`} className="relative">
                  <span className="tnum absolute top-0 -left-7 grid h-6 w-6 place-items-center rounded-full border border-brand-border bg-surface text-[11px] font-semibold text-brand">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-ink-muted">{step}</p>
                </li>
              ))}
            </ol>
          </Card>
        ) : null}

        {priorities.length === 0 && conflicts.length === 0 && sequence.length === 0 ? (
          <EmptyState
            icon={<SparkIcon className="h-5 w-5" />}
            title="The analysis produced no priorities"
            description="This happens when there is too little evidence to interpret — usually a very small site with no search data connected."
          />
        ) : null}
      </DetailMain>

      {/* ------------------------------------------------------------ rail */}
      <DetailRail>
        {missing.length > 0 ? <MissingContext items={missing} siteId={siteId} /> : null}

        <Card>
          <CardHeader title="This run" icon={<ClockIcon className="h-4 w-4" />} />
          <div className="divide-y divide-border-subtle">
            <DataRow label="Finished">
              <span className="text-ink">{formatRelative(run.finished_at ?? run.created_at)}</span>
            </DataRow>
            {run.duration_seconds !== null ? (
              <DataRow label="Took">
                <span className="tnum text-ink">{run.duration_seconds.toFixed(1)}s</span>
              </DataRow>
            ) : null}
            <DataRow label="Tokens">
              <span className="tnum text-ink">
                {formatNumber(run.input_tokens)} in / {formatNumber(run.output_tokens)} out
              </span>
            </DataRow>
            <DataRow label="Estimated cost">
              <span className="tnum text-ink">${run.estimated_cost_usd.toFixed(4)}</span>
            </DataRow>
          </div>
          {calls.length > 0 ? (
            <div className="mt-3 border-t border-border-subtle pt-3">
              <p className="mb-2 text-[11px] font-semibold tracking-wider text-ink-subtle uppercase">
                Models used
              </p>
              <ul className="space-y-1.5">
                {calls.map((call) => (
                  <li key={call.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-mono text-ink-muted" title={call.model}>
                      {call.model}
                    </span>
                    <span className="tnum shrink-0 text-ink-subtle">{call.latency_ms ?? '—'}ms</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="mt-3 border-t border-border-subtle pt-3 text-[11px] leading-relaxed text-ink-subtle">
            Cost is an estimate for budgeting; your provider's invoice is authoritative.
          </p>
        </Card>

        {run.steps.length > 0 ? (
          <Card>
            <CardHeader title="How it ran" icon={<CheckCircleIcon className="h-4 w-4" />} />
            <ol className="space-y-2.5">
              {run.steps.map((step) => (
                <li key={step.id} className="flex items-start gap-2.5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                      step.status === 'completed'
                        ? 'bg-success'
                        : step.status === 'skipped'
                          ? 'bg-ink-subtle'
                          : 'bg-danger',
                    )}
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-ink">{step.node.replace(/_/g, ' ')}</p>
                    {step.summary ? (
                      <p className="mt-0.5 text-[11px] leading-relaxed text-ink-subtle">
                        {step.summary}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        ) : null}

        {history.length > 1 ? (
          <Card>
            <CardHeader title="Earlier runs" icon={<ClockIcon className="h-4 w-4" />} />
            <ul className="divide-y divide-border-subtle">
              {history.slice(1).map((previous) => (
                <li
                  key={previous.id}
                  className="flex items-center justify-between gap-2 py-2 text-xs first:pt-0 last:pb-0"
                >
                  <span className="text-ink-muted">{formatRelative(previous.created_at)}</span>
                  <span className="tnum text-ink-subtle">
                    ${previous.estimated_cost_usd.toFixed(4)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <p className="px-1 text-[11px] leading-relaxed text-ink-subtle">
          Interpretation only. Nothing on this page has been applied to your website, and no part of
          it predicts or guarantees a ranking change.
        </p>
      </DetailRail>
    </DetailLayout>
  );
}

/**
 * The gaps the analysis flagged, capped by default.
 *
 * A low-confidence run can list a dozen of these, and left uncapped the card
 * dwarfs the run it belongs to — which buries the point rather than making it.
 */
function MissingContext({ items, siteId }: { items: string[]; siteId: string }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, VISIBLE_GAPS);

  return (
    <Card className="border-info/25 bg-info-subtle">
      <CardHeader
        title="What would sharpen this"
        icon={<InfoIcon className="h-4 w-4" />}
        description="Context the analysis needed and was not given."
      />
      <ul className="space-y-2">
        {shown.map((item) => (
          <li key={item} className="flex gap-2 text-xs leading-relaxed text-ink-muted">
            <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-info" />
            {item}
          </li>
        ))}
      </ul>
      {items.length > VISIBLE_GAPS ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2.5 text-xs font-medium text-brand hover:underline"
        >
          {expanded ? 'Show fewer' : `Show ${items.length - VISIBLE_GAPS} more`}
        </button>
      ) : null}
      <Link
        to={`/sites/${siteId}/settings`}
        className="mt-3 flex items-center gap-1 border-t border-info/20 pt-3 text-xs font-medium text-brand hover:underline"
      >
        Add business context
        <ArrowRightIcon className="h-3 w-3" />
      </Link>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex-1 px-4 py-3 text-center sm:px-6">
      <p className={cn('tnum text-lg font-semibold', value > 0 ? 'text-ink' : 'text-ink-subtle')}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] tracking-wide text-ink-subtle uppercase">{label}</p>
    </div>
  );
}

function PriorityCard({
  rank,
  priority,
  siteId,
}: {
  rank: number;
  priority: StrategicPriority;
  siteId: string;
}) {
  const tone = confidenceTone(priority.confidence);
  const accent = { success: 'bg-success', brand: 'bg-brand', warning: 'bg-warning' }[tone];
  const pill = {
    success: 'bg-success-subtle text-success',
    brand: 'bg-brand-subtle text-brand',
    warning: 'bg-warning-subtle text-warning',
  }[tone];

  return (
    <li className="card group relative overflow-hidden p-0 transition-shadow hover:shadow-raised">
      {/* A colour rail rather than a badge: it reads at a glance down a stack
          of cards without adding another element to each one. */}
      <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', accent)} />

      <div className="py-4 pr-5 pl-6">
        <div className="flex items-start gap-3">
          <span className="tnum mt-0.5 shrink-0 text-xs font-semibold text-ink-subtle">
            {String(rank).padStart(2, '0')}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-[15px] leading-snug font-semibold text-ink">{priority.title}</h3>
              <span
                className={cn(
                  'tnum shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  pill,
                )}
              >
                {Math.round(priority.confidence * 100)}%
              </span>
            </div>

            {priority.rationale ? (
              <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{priority.rationale}</p>
            ) : null}

            {priority.expected_outcome ? (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-ink-subtle">
                <TrendIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                {priority.expected_outcome}
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {priority.supporting_check_ids.length > 0 ? (
                <>
                  {/* Two words, but without them the chips are raw check ids
                      and a reader has no idea what they are looking at. */}
                  <span className="text-[10px] font-medium tracking-wider text-ink-subtle uppercase">
                    Grounded in
                  </span>
                  {priority.supporting_check_ids.map((checkId) => (
                    // Clickable on purpose: an interpretation you cannot
                    // trace back to a finding is one you cannot check.
                    <Link
                      key={checkId}
                      to={`/sites/${siteId}/issues?search=${encodeURIComponent(checkId)}`}
                      className="rounded-md bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-ink-subtle transition-colors hover:bg-brand-subtle hover:text-brand"
                    >
                      {checkId}
                    </Link>
                  ))}
                </>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-md bg-warning-subtle px-1.5 py-0.5 text-[11px] text-warning">
                  <AlertIcon className="h-3 w-3" />
                  unverified — no supporting check
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}
